import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadCurrentBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadCurrentBillTo";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    currentBillTo: getCurrentBillToState(state).value,
});

const mapDispatchToProps = {
    loadCurrentBillTo,
};

type Props = WidgetProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasShellContext;

export interface CheckoutBPNumberStyles {
    container?: GridContainerProps;
    headingGridItem?: GridItemProps;
    headingText?: TypographyPresentationProps;
    loadingOverlay?: LoadingOverlayProps;
    bpText?: TypographyProps;
}

export const checkoutBPNumberStyles: CheckoutBPNumberStyles = {
    container: {
        gap: 20,
        css: css`
            margin-bottom: 58px;
        `,
    },
    headingGridItem: {
        width: 12,
        css: css`
            align-items: center;
            background-color: #f6f5fb;
            height: 53px;
        `,
    },
    headingText: {
        variant: "h5",
        size: 18,
        css: css`
            margin-bottom: 0;
            font-weight: 900;
            letter-spacing: 2px;
            line-height: 22px;
            text-transform: uppercase;
        `,
    },
    loadingOverlay: {
        css: css`
            width: 100%;
        `,
    },
    bpText: {
        as: "p",
        size: 14,
        lineHeight: "24px",
        color: "text.main",
    },
};

const styles = checkoutBPNumberStyles;

const CheckoutBPNumber: FC<Props> = ({ currentBillTo, loadCurrentBillTo, shellContext }) => {
    if (!currentBillTo && !shellContext.isInShell) {
        loadCurrentBillTo();
    }

    const bpNumber = currentBillTo?.properties.bpNumber;

    return (
        <LoadingOverlay {...styles.loadingOverlay} loading={!currentBillTo && !shellContext.isInShell}>
            <GridContainer {...styles.container}>
                <GridItem {...styles.headingGridItem}>
                    <Typography {...styles.headingText} as="h3">
                        {translate("Husky Business Partner Number")}
                    </Typography>
                </GridItem>
                <GridItem width={12 as GridWidths}>
                    <Typography {...styles.bpText}>{bpNumber}</Typography>
                </GridItem>
            </GridContainer>
        </LoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(CheckoutBPNumber)),
    definition: {
        group: "Checkout" as any,
        allowedContexts: ["CheckoutShippingPage", "CheckoutReviewAndSubmitPage"],
        displayName: "Husky BP Number",
    },
};

export default widgetModule;
